import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(params.get("expired") ? "Your session expired. Please login again." : "");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await login(username.trim().toLowerCase(), password);
      toast.success("Login successful");
      navigate("/");
    } catch (e2) {
      setError(errMsg(e2));
    } finally { setBusy(false); }
  };

  const sendForgot = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/forgot-password", { username: username.trim().toLowerCase() });
      toast.success(data.message);
      setForgot(false);
    } catch (e2) { setError(errMsg(e2)); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-[#0b1f4b] p-12 text-white lg:flex">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded bg-[#1d4ed8] text-lg font-bold text-white">स्व</div>
          <div>
            <div className="font-serif text-lg font-semibold">Swadhar Scheme</div>
            <div className="text-xs text-blue-200">स्वाधार योजना · जिल्हा लातूर</div>
          </div>
        </div>
        <div className="max-w-md space-y-5">
          <h1 className="font-serif text-4xl leading-tight lg:text-5xl">Beneficiary Database Management System</h1>
          <p className="text-sm leading-relaxed text-blue-100">
            एकच मास्टर लाभार्थी डेटाबेस — पात्रता, बँक तपशील, हप्ते, उपस्थिती, कागदपत्रे आणि अहवाल एकाच ठिकाणी.
          </p>
          <div className="flex items-center gap-2 text-xs text-blue-200"><ShieldCheck size={15} /> Authorised government staff only · All activity is audited</div>
        </div>
        <div className="text-xs text-blue-300">© {new Date().getFullYear()} District Administration, Latur</div>
      </div>

      <div className="flex items-center justify-center bg-[#f3f6fc] p-6">
        <div className="w-full max-w-sm">
          <div className="mb-5 flex items-center gap-3 lg:hidden">
            <div className="grid h-10 w-10 place-items-center rounded bg-[#0b1f4b] font-bold text-white">स्व</div>
            <div className="leading-tight">
              <div className="font-serif text-base font-semibold text-[#0b1f4b]">Swadhar Scheme BDMS</div>
              <div className="text-[11px] text-slate-500">स्वाधार योजना · जिल्हा लातूर</div>
            </div>
          </div>
          <div className="rounded-xl border border-[#1d4ed8]/15 bg-white p-7 shadow-lg shadow-[#0b1f4b]/5">
          <h2 className="font-serif text-2xl font-semibold text-[#0b1f4b]">{forgot ? "Forgot Password" : "Administrator Login"}</h2>
          <p className="mt-1 text-sm text-slate-500">{forgot ? "Enter your username to request a reset token." : "प्रवेश करण्यासाठी वापरकर्ता नाव व पासवर्ड टाका."}</p>

          <form onSubmit={forgot ? sendForgot : submit} className="mt-6 space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Username / वापरकर्ता नाव</label>
              <input data-testid="login-username" value={username} onChange={(e) => setUsername(e.target.value)} required
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#1d4ed8] focus:ring-1 focus:ring-[#1d4ed8]" placeholder="Enter your username" />
            </div>
            {!forgot && (
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Password / पासवर्ड</label>
                <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#1d4ed8] focus:ring-1 focus:ring-[#1d4ed8]" placeholder="••••••••" />
              </div>
            )}
            {error && <div data-testid="login-error" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
            <button data-testid={forgot ? "forgot-submit" : "login-submit"} disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-[#0b1f4b] py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#1d4ed8] disabled:opacity-60">
              {busy && <Loader2 size={16} className="animate-spin" />} {forgot ? "Request Reset Token" : "Login"}
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <button data-testid="forgot-toggle" onClick={() => { setForgot(!forgot); setError(""); }} className="text-xs font-medium text-[#1d4ed8] underline">
              {forgot ? "Back to login" : "Forgot password?"}
            </button>
            <span className="text-[11px] text-slate-400">Authorised users only</span>
          </div>
          <div className="mt-4 rounded-md border border-[#1d4ed8]/20 bg-[#1d4ed8]/5 p-3 text-xs text-[#0b1f4b]">
            विद्यार्थ्यांसाठी / For students: <a data-testid="know-status-link" href="/status" className="font-semibold underline">Know Your Swadhar Status →</a>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}
